import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import { toTestType } from '@risksmart-app/domain/src/types/consts/test-type';
import type { FC, ReactNode } from 'react';
import { PageWrapper } from 'src/components/form/form/PageWrapper';
import { useGetInternalAuditTestResultById } from 'src/hooks/queries';
import TestResultForm from 'src/pages/controls/update/forms/TestResultForm';
import type { TestResultFormFieldsData } from 'src/pages/controls/update/forms/testResultSchema';
import { defaultValues } from 'src/pages/controls/update/forms/testResultSchema';
import type { ObjectWithContributors } from 'src/rbac/Permission';
import type { UserOption } from 'src/schemas/global';

import { ModalBodyWrapper } from '@/components/form/form/ModalBodyWrapper';
import { useMultiParentFileUpdate } from '@/hooks/mutations/file/useFileUpdate';
import { useInsertControlTestInternalAuditResult } from '@/hooks/mutations/internal-audit-result/useInsertControlTestInternalAuditResult';
import { useUpdateControlTestInternalAuditResult } from '@/hooks/mutations/internal-audit-result/useUpdateControlTestInternalAuditResult';
import { saveFilesForOptionalCreatedEntity } from '@/utils/createWithFiles';
import { internalAuditReportResultsUrl } from '@/utils/urls';
type Props = {
  readonly: boolean;
  navigateToResults: boolean;
  parentId: string;
  controlTestId?: string;
  assessedItem?: ObjectWithContributors;
  id?: string;
  onDismiss?: (saved: boolean) => void;
  beforeFieldsSlot?: ReactNode;
  controlIds?: string[];
  header?: string;
  isModalForm?: boolean;
};

const ConnectedInternalAuditControlTestResultForm: FC<Props> = (props) => {
  const Id = props.id;
  let Ids: string[] = [];
  if (Id) {
    Ids.push(Id);
  }
  const { data, loading: loadingInternalAuditTestResult } =
    useGetInternalAuditTestResultById({
      queryArgs: { id: Id ?? '' },
      shouldSkip: !Id,
    });

  const testResult = data?.control_test_internal_audit_result[0];
  const { updateFiles } = useMultiParentFileUpdate();

  const { insertControlTestInternalAuditResult: insert } =
    useInsertControlTestInternalAuditResult();
  const { updateControlTestInternalAuditResult: update } =
    useUpdateControlTestInternalAuditResult();

  const onSave = async (data: TestResultFormFieldsData) => {
    const { files, ...rest } = data;
    if (testResult) {
      const result = await update({
        Id: testResult.Id,
        OriginalTimestamp: testResult.ModifiedAtTimestamp,
        ParentControlId: testResult.ParentControlId,
        Title: rest.Title,
        TestType: rest.TestType,
        Description: rest.Description,
        DesignEffectiveness: rest.DesignEffectiveness,
        PerformanceEffectiveness: rest.PerformanceEffectiveness,
        OverallEffectiveness: rest.OverallEffectiveness,
        Submitter: rest.Submitter.value,
        TestDate: rest.TestDate,
        CustomAttributeData: rest.CustomAttributeData ?? null,
      });
      if (!result.updateControlTestInternalAuditResultApi?.Id) {
        throw new Error(
          'Records not updated. Record may have been updated by another user'
        );
      }
    } else {
      if (!props.parentId) {
        throw new Error('An internal audit report is required');
      }
      const result = await insert({
        InternalAuditReportId: props.parentId,
        ControlIds: rest.ParentControlIds.map((p) => p.value),
        Title: rest.Title,
        TestType: rest.TestType,
        Description: rest.Description,
        DesignEffectiveness: rest.DesignEffectiveness,
        PerformanceEffectiveness: rest.PerformanceEffectiveness,
        OverallEffectiveness: rest.OverallEffectiveness,
        Submitter: rest.Submitter.value,
        TestDate: rest.TestDate,
        CustomAttributeData: rest.CustomAttributeData ?? null,
      });
      if (!result.insertChildControlTestInternalAuditResult?.Ids?.length) {
        throw new Error('Id not found');
      }
      Ids = result.insertChildControlTestInternalAuditResult?.Ids;
    }
    const savedId = Ids[0];
    if (!savedId) {
      throw new Error('Id not found');
    }

    const createdEntity =
      !testResult && props.parentId
        ? {
            id: savedId,
            destination: internalAuditReportResultsUrl(props.parentId),
          }
        : undefined;
    const saveFiles = () =>
      updateFiles({
        parentType: Parent_Type_Enum.ControlTestInternalAuditResult,
        parentIds: Ids,
        originalFiles: testResult?.files.map((f) => f.file),
        selectedFiles: files,
      });
    await saveFilesForOptionalCreatedEntity({ createdEntity, saveFiles });

    return {
      savedIds: Ids,
      createdEntity,
      successNavigation:
        props.navigateToResults && props.parentId
          ? { destination: internalAuditReportResultsUrl(props.parentId) }
          : undefined,
    };
  };

  return (
    <div className={'pb-5'}>
      <TestResultForm
        {...props}
        renderTemplate={(renderProps) =>
          props.isModalForm ? (
            <ModalBodyWrapper {...renderProps} />
          ) : (
            <PageWrapper {...renderProps} />
          )
        }
        defaultValues={{
          ...defaultValues,
        }}
        assessmentMode={'internal_audit_report'}
        values={{
          ...defaultValues,
          ...testResult,
          TestType: toTestType(testResult?.TestType),
          files: testResult?.files.map((rf) => rf.file) ?? [],
          Submitter: testResult?.Submitter
            ? { value: testResult.Submitter, type: 'user' }
            : (undefined as unknown as UserOption),
          ParentControlIds: testResult
            ? [{ value: testResult.ParentControlId }]
            : props.controlIds
              ? props.controlIds.map((c) => ({
                  value: c,
                }))
              : [],
        }}
        onSave={onSave}
        onDismiss={props.onDismiss}
        readOnly={props.readonly || loadingInternalAuditTestResult}
        disableControlSelect={props.id !== undefined}
      />
    </div>
  );
};

export default ConnectedInternalAuditControlTestResultForm;
